// Stores
import { useResult } from "../../../../shared/formContext/hooks/useResult";

// Components
import Box from "../../../../components/Box/Box.js";
import Tabs from "../../../../components/Tabs/Tabs.js";
import Cta from "../../../../components/Cta/Cta";
import ShareFullLink from "../../../../shared/components/ShareFullLink";

// Tabs
import TabComposition from "../Tabs/TabComposition";
import TabComparison from "../Tabs/TabComparison";

// Utils
import { utils } from "../../../../helpers/index.js";

// WP react stuff
import { useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";

/** Which of the two required-savings scenarios the external API returns is shown in the top box. */
export type Scenario = "prudente" | "ampliado";

export interface OperatingCosts {
  taxes: Record<string, number>;
  taxesTotal: number;
  fixedCosts: Record<string, number>;
  fixedCostsTotal: number;
  total: number;
}

export interface ScenarioResult {
  ltv: number;
  equityRequired: number;
  downPaymentValue: number;
  loanAmount: number;
  operatingCosts: OperatingCosts;
}

export interface QuoteEquityMetadata {
  region: string;
  property: { condition: string };
  propertyValue: number;
  referenceDate: string;
}

export interface QuoteEquityScenarios {
  prudente: ScenarioResult;
  ampliado: ScenarioResult;
}

const LABELS = {
  valueToConsider: __("Valor a considerar num", "df-simulators"),
  prudente: __("Cenário prudente", "df-simulators"),
  ampliado: __("Cenário ampliado", "df-simulators"),
  compositionTab: __("Composição do cenário", "df-simulators"),
  comparisonTab: __("Comparação entre cenários", "df-simulators"),
};

const EquityScenarioSelector: React.FC<{
  scenario: Scenario;
  setScenario: (scenario: Scenario) => void;
}> = ({ scenario, setScenario }) => (
  <>
    {LABELS.valueToConsider}
    <select
      onChange={(e) => setScenario(e.target.value as Scenario)}
      value={scenario}
    >
      <option value="prudente">{LABELS.prudente}</option>
      <option value="ampliado">{LABELS.ampliado}</option>
    </select>
  </>
);

const QuoteEquity: React.FC = () => {
  const { resultData } = useResult();
  const { response, loading } = resultData;
  const [scenario, setScenario] = useState<Scenario>("prudente");

  const data = response?.data?.result;
  const metadata: QuoteEquityMetadata | undefined = data?.metadata;
  const scenarios: QuoteEquityScenarios | undefined = data?.results;
  const scenarioData = scenarios?.[scenario];

  return (
    <div className="quote-equity">
      {scenarioData && (
        <Box
          grid={undefined}
          loading={loading}
          backgroundColor={undefined}
          className="quote-equity__top-box"
          textColor={undefined}
          vertical={undefined}
          collapsible={undefined}
          noPadding={undefined}
          header={{
            titleAlt: (
              <EquityScenarioSelector
                scenario={scenario}
                setScenario={setScenario}
              />
            ),
            color: "orange-100",
          }}
        >
          <div className="text-center">
            <div className="icon-content quote-equity__top-box__icon-content">
              <div className="icon-content__icon mr-2">
                <img
                  src="/wp-content/plugins/df-simulators/src/blocks/quote-equity/assets/quote-equity.webp"
                  alt=""
                  className="quote-equity__top-box__icon-image"
                />
              </div>
              <div className="icon-content__content">
                <div className="icon-content__content__big-text text-orange-100">
                  {utils.toEuro(scenarioData.equityRequired)}
                </div>
              </div>
            </div>
          </div>
        </Box>
      )}

      {scenarioData && metadata && scenarios && (
        <Tabs
          loading={loading}
          data={[
            {
              heading: LABELS.compositionTab,
              content: <TabComposition scenarioData={scenarioData} />,
            },
            {
              heading: LABELS.comparisonTab,
              content: <TabComparison scenarios={scenarios} />,
            },
          ]}
        />
      )}

      {/* Share Simulation button */}
      <ShareFullLink />

      {/* Call-to-Action - Standard project pattern */}
      <Cta />
    </div>
  );
};

export default QuoteEquity;
